/**
 * steps/StrategyDefinitionStep.tsx
 * Strategy definition step for the onboarding wizard
 *
 * Collects basic strategy information:
 * - Strategy name (required)
 * - Strategy description (optional)
 * - Trading philosophy (optional)
 * - Uses HeroUI inputs with built-in helper text
 * - Integrates with draft persistence system
 */

// 1. External dependencies
import React, { useState, useEffect } from 'react';
import { Target, Info } from 'lucide-react';

// 2. Internal absolute imports
import { Input, TextArea, Card, CardBody, CardHeader, CardFooter, Accordion, AccordionItem } from '@enfomentis/ui';

// 3. Relative imports
import { useOnboardingData } from '../hooks/useOnboardingData';

/**
 * Strategy definition step component
 */
export const StrategyDefinitionStep: React.FC = () => {
  const { draft, updateTrading } = useOnboardingData();

  // Local form state
  const [formData, setFormData] = useState({
    strategyName: '',
    strategyDescription: '',
    strategyPhilosophy: ''
  });

  // Initialize from draft
  useEffect(() => {
    if (draft?.trading?.strategy) {
      setFormData({
        strategyName: draft.trading.strategy.name || '',
        strategyDescription: draft.trading.strategy.description || '',
        strategyPhilosophy: draft.trading.strategy.philosophy || ''
      });
    }
  }, [draft]);

  /**
   * Handle strategy name changes
   */
  const handleStrategyNameChange = (value: string): void => {
    const newFormData = { ...formData, strategyName: value };
    setFormData(newFormData);

    // Update draft immediately
    updateTrading({
      strategy: {
        name: value,
        description: formData.strategyDescription,
        philosophy: formData.strategyPhilosophy
      }
    });
  };

  /**
   * Handle strategy description changes
   */
  const handleDescriptionChange = (value: string): void => {
    const newFormData = { ...formData, strategyDescription: value };
    setFormData(newFormData);

    // Update draft immediately
    updateTrading({
      strategy: {
        name: formData.strategyName,
        description: value,
        philosophy: formData.strategyPhilosophy
      }
    });
  };

  /**
   * Handle philosophy changes
   */
  const handlePhilosophyChange = (value: string): void => {
    const newFormData = { ...formData, strategyPhilosophy: value };
    setFormData(newFormData);

    // Update draft immediately
    updateTrading({
      strategy: {
        name: formData.strategyName,
        description: formData.strategyDescription,
        philosophy: value
      }
    });
  };

  return (
    <Card
      radius="none"
      shadow="none"
      className="h-full max-h-[100dvh] w-full flex flex-col border-0">

      <CardHeader>
        <div className="flex items-center gap-2">
          <Target className="w-5 h-5 text-primary" />
          <span className="text-large font-semibold">Define Your Strategy</span>
        </div>
      </CardHeader>

      <CardBody className="space-y-6">
        <div className="max-w-2xl mx-auto px-2">
          {/* Form Fields */}
          <div className="space-y-6">
            {/* Strategy Name */}
            <Input
              label="Strategy Name"
              placeholder="e.g., Liquidity ATT, Breakout Momentum, ICT Setup"
              value={formData.strategyName}
              onValueChange={handleStrategyNameChange}
              isRequired
              variant="bordered"
              size="lg"
              description="Give your strategy a memorable name"
              errorMessage={
                formData.strategyName.length > 0 && formData.strategyName.trim().length < 2
                  ? "Strategy name must be at least 2 characters"
                  : undefined
              }
              isInvalid={formData.strategyName.length > 0 && formData.strategyName.trim().length < 2}
            />

            {/* Strategy Description */}
            <TextArea
              label="Strategy Description"
              placeholder="Optional: Describe what this strategy does and when to use it"
              value={formData.strategyDescription}
              onValueChange={handleDescriptionChange}
              variant="bordered"
              size="lg"
              minRows={3}
              maxRows={6}
              description="Help yourself remember the key aspects of this strategy (completely optional)"
            />

            {/* Trading Philosophy */}
            <TextArea
              label="Trading Philosophy"
              placeholder="Optional: What principles guide this strategy?"
              value={formData.strategyPhilosophy}
              onValueChange={handlePhilosophyChange}
              variant="bordered"
              size="lg"
              minRows={3}
              maxRows={6}
              description="Your trading beliefs and approach with this strategy (completely optional)"
            />

            {/* Validation Status */}
            {formData.strategyName.trim().length >= 2 && (
              <div className="flex items-center gap-2 text-success text-sm">
                <div className="w-1.5 h-1.5 rounded-full bg-success"></div>
                Strategy definition looks good! Ready to continue.
              </div>
            )}
          </div>
        </div>
      </CardBody>

      <CardFooter className="flex-shrink-0 border-t border-default-200">
        <Accordion variant="light" fullWidth isCompact>
          <AccordionItem
            key="why-needed"
            textValue="Why define a strategy?"
            title={
              <div className="flex items-center gap-2">
                <Info size={16} className="text-text-secondary" />
                <span className="text-sm font-medium text-text-secondary">
                  Why define a strategy?
                </span>
              </div>
            }
          >
            <ul className="text-sm text-text-secondary space-y-1 pb-2">
              <li>• A strategy groups related playbooks under a unified approach</li>
              <li>• Helps organize your trading methodology and track what works</li>
              <li>• You can refine your philosophy and description over time</li>
            </ul>
          </AccordionItem>
        </Accordion>
      </CardFooter>
    </Card>
  );
};
